import { Box, Button, Text } from '@wix/design-system'
import { FlowContext, useContent, useNext } from '@wix/funnel-builder-lizard'
import {
  ArrowRight,
  Cutlery,
  Extensions,
  Heart,
  HelpCircle,
  Home,
  Library,
  Mobile,
  Scenery,
  TShirt,
} from '@wix/wix-ui-icons-common'
import classNames from 'classnames'
import React, { FC, useEffect } from 'react'

import { usePageLogger } from '../../../../bi/mfi'
import { useFlowContext } from '../../../../hooks/useFlowContext'
import { WixMobileBackButton } from '../../../reusable/WixMobileBackButton/WixMobileBackButton'
import { WixMobileContentWrapper } from '../../../reusable/WixMobileContentWrapper/WixMobileContentWrapper'
import { WixMobileFooter } from '../../../reusable/WixMobileFooter/WixMobileFooter'
import { WixMobileHeader } from '../../../reusable/WixMobileHeader/WixMobileHeader'
import { WixMobileHeading } from '../../../reusable/WixMobileHeading/WixMobileHeading'
import { WixMobilePage } from '../../../reusable/WixMobilePage/WixMobilePage'
import { WixMobileSkipButton } from '../../../reusable/WixMobileSkipButton/WixMobileSkipButton'
import { WixMobileStepProgressBar } from '../../../reusable/WixMobileStepProgressBar/WixMobileStepProgressBar'
import { CategoryItem, Content } from './StoresFunnelChooseCategory.data'
import { storesFunnelChooseCategoryDataHooks as DataHooks } from './StoresFunnelChooseCategory.dataHooks'
import s from './StoresFunnelChooseCategory.scss'

export const StoresFunnelChooseCategory: FC = () => {
  const { content } = useContent<Content>()
  const next = useNext()
  const pageLogger = usePageLogger()

  const { title, subtitle } = content.question.content

  useEffect(() => {
    void pageLogger.screenView()
  }, [pageLogger])

  const onSkipButtonClick = () => {
    pageLogger.skip({
      payload: {},
    })

    next({ [content.question.questionKey]: { skipped: true } })
  }

  const onNextButtonClick = () => {
    pageLogger.answer({
      answer: { values: [] },
    })

    next({ [content.question.questionKey]: { values: [] } })
  }

  const onBackButtonClick = () => {
    pageLogger.back({
      payload: {},
    })

    window.history.back()
  }

  const onSelectCategory = (categoryKey: string) => {
    pageLogger.answer({
      answer: { categoryKey },
    })

    next({ [content.question.questionKey]: { values: [categoryKey] } })
  }

  return (
    <WixMobilePage>
      <WixMobileHeader
        leftContent={
          (content.back && (
            <WixMobileBackButton dataHook={DataHooks.back()} onBack={onBackButtonClick} label={content.back} />
          )) ||
          undefined
        }
        rightContent={
          (content.skip && (
            <WixMobileSkipButton dataHook={DataHooks.skip()} onSkip={onSkipButtonClick} label={content.skip} />
          )) ||
          undefined
        }
      />
      <WixMobileContentWrapper>
        {content.stepProgressBar.content.visible && (
          <WixMobileStepProgressBar
            current={content.stepProgressBar.content.current}
            total={content.stepProgressBar.content.total}
            dataHook={DataHooks.stepProgressBar()}
          />
        )}
        <WixMobileHeading dataHook={DataHooks.title()}>{title}</WixMobileHeading>
        <Text dataHook={DataHooks.subtitle()} className={s.StoresFunnelChooseCategory__subtitle}>
          {subtitle}
        </Text>
        <Box className={s.StoresFunnelChooseCategory}>
          {content.question.options.map((option, index) => (
            <CategoryItemComponent key={option.optionKey} item={option} onSelectCategory={onSelectCategory} />
          ))}
        </Box>

        {content.somethingElse && (
          <div
            className={s.StoresFunnelChooseCategory__itemContent_fullWidth}
            onClick={() => onSelectCategory('something_else')}
          >
            <Box gap="8px" verticalAlign="middle">
              {/* <HelpCircle />*/}
              <Text>{content.somethingElse}</Text>
            </Box>
            <ArrowRight color="#116DFF" />
          </div>
        )}
      </WixMobileContentWrapper>
      {content.next && (
        <WixMobileFooter>
          <Button size="large" fullWidth onClick={onNextButtonClick} dataHook={DataHooks.next()}>
            {content.next}
          </Button>
        </WixMobileFooter>
      )}
    </WixMobilePage>
  )
}

type CategoryItemProps = {
  item: CategoryItem
  onSelectCategory: (categoryKey: string) => void
}

const CategoryItemComponent: FC<CategoryItemProps> = ({ item, onSelectCategory }) => {
  return (
    <div
      className={classNames(s.StoresFunnelChooseCategory__item)}
      onClick={() => onSelectCategory(item.optionKey)}
      data-hook={DataHooks.categoryItem({ key: item.optionKey })}
    >
      <Box
        width="100%"
        align="space-between"
        verticalAlign="middle"
        data-hook={DataHooks.categoryIcon({ key: item.optionKey })}
      >
        {OptionKeyToIcon[item.optionKey]}
        <ArrowRight color="#116DFF" />
      </Box>
      <Text data-hook={DataHooks.categoryTitle({ key: item.optionKey })}>{item.title}</Text>
    </div>
  )
}

const OptionKeyToIcon: Record<string, React.ReactNode> = {
  fashion_apparel: <TShirt />,
  beauty_personal_care: <Heart />,
  home_living: <Home />,
  electronics_gadgets: <Mobile />,
  sports_outdoors: <Scenery />,
  food_beverages: <Cutlery />,
  toys_kids_baby: <Extensions />,
  books_media_art: <Library />,
  something_else: <HelpCircle />,
}
